import React from "react";
import { Link } from "react-router-dom";
import siteConfig from "../config/siteConfig";
import "../css/privacy.scss";

function Privacy() {
  const lastUpdated = siteConfig.policies?.lastUpdated || "August 14, 2026";

  const toTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <main className="privacy policy-page">
      <header className="policy-page__hero">
        <p className="policy-page__kicker">Legal</p>
        <h1>Privacy policy</h1>
        <p>
          How {siteConfig.brandName} collects, uses and protects information
          when you browse, shop or contact us.
        </p>
        <span>Last updated {lastUpdated}</span>
      </header>

      <div className="policy-page__layout">
        <nav className="policy-page__nav" aria-label="Privacy policy sections">
          <span>On this page</span>
          <a href="#privacy-collect">Information we collect</a>
          <a href="#privacy-use">How we use it</a>
          <a href="#privacy-share">How we share it</a>
          <a href="#privacy-rights">Your choices</a>
          <a href="#privacy-contact">Contact</a>
        </nav>

        <article className="policy-page__content">
          <section className="policy-page__lead" aria-label="Privacy overview">
            <p>
              This policy applies to information handled through our website,
              checkout and customer-care channels. By using
              our services, you acknowledge the practices described below.
            </p>
          </section>

          <section id="privacy-collect">
            <p className="policy-page__number">01</p>
            <h2>Information we collect</h2>
            <p>We collect information in three main ways:</p>
            <ul>
              <li>
                <strong>Information you provide,</strong> including your name,
                contact details, shipping and billing address, account choices,
                reviews and messages to customer care.
              </li>
              <li>
                <strong>Order information,</strong> such as products purchased,
                transaction status and returns. Payment card details are handled
                by our payment providers and are not stored in full by us.
              </li>
              <li>
                <strong>Technical information,</strong> including device and
                browser type, approximate location, IP address, pages viewed and
                interactions collected through cookies or similar technology.
              </li>
            </ul>
          </section>

          <section id="privacy-use">
            <p className="policy-page__number">02</p>
            <h2>How we use information</h2>
            <p>
              We use information to process and deliver orders, provide service
              updates, prevent fraud, answer questions and meet legal duties. We
              also use it to operate and improve our store, understand which
              products are useful to customers and keep core shopping features reliable.
            </p>
            <p>
              Where applicable, our legal grounds include performing a contract,
              complying with law, pursuing legitimate business interests and
              obtaining your consent.
            </p>
          </section>

          <section>
            <p className="policy-page__number">03</p>
            <h2>Cookies and analytics</h2>
            <p>
              Essential cookies keep the cart and checkout working. Optional
              limited analytics tools may help us understand site use and improve
              performance. You can manage cookies through the controls made
              available on the site or in your browser. Blocking essential
              cookies may affect core shopping features.
            </p>
          </section>

          <section id="privacy-share">
            <p className="policy-page__number">04</p>
            <h2>How we share information</h2>
            <p>
              We share only what is reasonably necessary with providers that
              support payments, hosting, fulfillment, delivery, analytics and
              customer service. We may also disclose information
              to comply with law, protect rights and safety, or complete a
              corporate transaction. Providers must handle information for the
              agreed purpose and safeguard it. We do not currently sell personal
              information for money or share it for cross-context behavioral
              advertising as those terms are defined by applicable privacy law.
            </p>
          </section>

          <section>
            <p className="policy-page__number">05</p>
            <h2>Retention and security</h2>
            <p>
              We retain information only for as long as needed for the purpose it
              was collected, including order administration, tax, dispute and
              warranty requirements. We use administrative, technical and
              physical safeguards designed to protect information. No online
              system is completely secure, so please use unique passwords and
              contact us promptly if you suspect unauthorized activity.
            </p>
          </section>

          <section id="privacy-rights">
            <p className="policy-page__number">06</p>
            <h2>Your choices and rights</h2>
            <p>
              Depending on your location, you may ask to access, correct, delete,
              restrict or receive a copy of your personal information, or object
              to certain processing. We may need to verify your identity
              before completing a request, and some information may be retained
              where law allows or requires it.
            </p>
            <p>
              To make a request and review regional details, visit our{" "}
              <Link to="/data-rights">data rights &amp; requests policy</Link>.
            </p>
          </section>

          <section>
            <p className="policy-page__number">07</p>
            <h2>Children and international transfers</h2>
            <p>
              Our store is intended for adults and is not directed to children
              under 13. We do not knowingly collect children&apos;s personal
              information. Information may be processed outside your country;
              when required, we use recognized safeguards for those transfers.
            </p>
          </section>

          <section id="privacy-contact">
            <p className="policy-page__number">08</p>
            <h2>Changes and contact</h2>
            <p>
              We may update this policy as our services or legal requirements
              change. The date above shows the latest revision. For a privacy
              request or question, email{" "}
              <a href={`mailto:${siteConfig.contact.email}`}>
                {siteConfig.contact.email}
              </a>. Our business country is {siteConfig.contact.country}; a public
              business address is not currently available.
            </p>
          </section>

          <div className="policy-page__related">
            <span>Related policies</span>
            <Link to="/terms" onClick={toTop}>Terms &amp; conditions</Link>
            <Link to="/data-rights" onClick={toTop}>Data rights</Link>
            <Link to="/cookies" onClick={toTop}>Cookies</Link>
          </div>
        </article>
      </div>
    </main>
  );
}

export default Privacy;
